import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid } from '@mui/material';
import classNames from 'classnames';
import { SGSCheckboxCtr, SGSForm, SGSFormInput, SGSFormLayout } from 'components';
import DownloadPdfButton from 'components/SGSDownloadPdfButton';
import SGSFileUploader from 'components/SGSFileUploader';
import { useArretTravailFormContext } from 'features/arretTravail/contexts/ArretTravailFormProvider';
import useValidationEffects from 'features/arretTravail/hooks/useValidationEffects';
import { TypeDeclaration } from 'features/arretTravail/models/typeChoix';
import { getRematerialization, getRematerializationRCSS } from 'features/arretTravail/services/workStoppageService';
import { FC, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import styles from '../../../css/ArretTravail.module.scss';

type CertificationData = {
    fullname: string;
    email: string;
    isCertified: boolean;
};

type DocumentData = {
    filename: string;
    data: string; // This can be Base64 encoded data
};

type ValidationSchema = {
    certification: CertificationData;
    documents: { [key: string]: DocumentData | null };
};

// Max file size in bytes (e.g., 2MB)
const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
const SUPPORTED_FILE_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];

type Props = {
    store: any;
    onSubmit: SubmitHandler<ValidationSchema>;
    actions?: JSX.Element;
};

const FormCertification: FC<Props> = ({ store, onSubmit, actions }) => {
    const { activeStep, setStepFailed, fileList } = useArretTravailFormContext();
    const [isDownloading, setIsDownloading] = useState(false);
    const [isDownloadingSS, setIsDownloadingSS] = useState(false);

    // Validation schema
    const validationSchema: Yup.SchemaOf<ValidationSchema> = Yup.object().shape({
        certification: Yup.object().shape({
            fullname: Yup.string().required('Le nom complet est requis'),
            email: Yup.string().email('Format email invalide').required('L’email est requis'),
            isCertified: Yup.boolean().oneOf([true], 'Vous devez certifier pour continuer'),
        }),
        documents: Yup.array()
            .of(
                Yup.lazy((value): Yup.SchemaOf<any> => {
                    // Allow null values in the documents array
                    if (value === null) {
                        return Yup.mixed().nullable(true);
                    }

                    // Apply object schema for non-null values
                    return Yup.object().shape({
                        filename: Yup.string()
                            .nullable()
                            .matches(/\.(pdf|doc|docx|jpeg|jpg|png)$/i, 'Le fichier doit être au format PDF, DOC, DOCX, JPEG, JPG, ou PNG'),
                        data: Yup.string()
                            .nullable()
                            .test('file-size', 'La taille du fichier doit être supérieure à 0 octets et ne doit pas dépasser 2 Mo', (value) => {
                                if (!value) return true; // Skip if empty
                                const base64Content = value.split(',')[1]; // Strip off the base64 header
                                const fileSize = Buffer.byteLength(base64Content, 'base64');
                                return fileSize > 18 && fileSize <= MAX_FILE_SIZE;
                            })
                            .test('file-type', 'Seuls les fichiers PDF, JPEG, ou PNG sont acceptés', (value) => {
                                if (!value) return true; // Skip if empty
                                const fileType = value.split(';')[0].split(':')[1]; // Extract MIME type
                                return SUPPORTED_FILE_TYPES.includes(fileType);
                            }),
                    });
                }) as unknown as Yup.SchemaOf<any>, // Explicitly cast as `unknown` first, then `Yup.SchemaOf<any>`
            )
            .optional(),
    });

    // Initialize form methods
    const methods = useForm<ValidationSchema>({
        resolver: yupResolver(validationSchema),
        defaultValues: store,
    });

    const {
        handleSubmit,
        formState: { errors },
        control,
    } = methods;

    console.log(errors);

    // Handle validation effects
    useValidationEffects({ errors, activeStep, setStepFailed, control });

    // Render list of file inputs
    const renderFileList = () => <Box>{fileList.length > 0 && fileList.map((file, index) => <SGSFileUploader key={index} field={file} index={index.toString()} fieldName={'documents'} />)}</Box>;

    const handleDownloadRC = async () => {
        setIsDownloading(true);
        try {
            const response = await getRematerialization(store);
            const blob = new Blob([response as BlobPart], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'rematerialization.pdf'); // Customize filename as needed
            document.body.appendChild(link);

            // Adding a slight delay to ensure link is ready for download
            setTimeout(() => {
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url); // Clean up
            }, 100); // 100 ms delay
        } catch (error) {
            console.error('Error downloading PDF:', error);
        }
        setIsDownloading(false);
    };

    const handleDownloadRCSS = async () => {
        setIsDownloadingSS(true);
        try {
            const response = await getRematerializationRCSS(store);
            const blob = new Blob([response as BlobPart], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'rematerialization_RCSS.pdf'); // Customize filename as needed
            document.body.appendChild(link);

            // Adding a slight delay to ensure link is ready for download
            setTimeout(() => {
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url); // Clean up
            }, 100); // 100 ms delay
        } catch (error) {
            console.error('Error downloading PDF:', error);
        }
        setIsDownloadingSS(false);
    };

    return (
        <FormProvider {...methods}>
            <SGSFormLayout onSubmit={handleSubmit(onSubmit)} actions={actions}>
                <Grid container justifyContent="center">
                    <Grid item xs={12} md={6}>
                        {fileList.length > 0 && (
                            <>
                                <Box className={classNames(styles.title, 'mb-5')}>Documents</Box>
                                <Box className={'mb-5'}>{renderFileList()}</Box>
                            </>
                        )}
                        <Box className={styles.title}>Certification</Box>
                        <Box className={classNames(styles.subTitle, 'mb-4')}>
                            IMPORTANT : Avant toute validation, nous vous recommandons de bien vouloir vérifier les informations saisies précédemment.
                        </Box>

                        <Box className="mb-5" display="flex" justifyContent="space-between" alignItems="center">
                            <DownloadPdfButton handleDownload={handleDownloadRC} isDownloading={isDownloading} title={'Aperçu de la déclaration à SGSanté'} />
                            {store.typeDeclarationCod === TypeDeclaration.Choice2 && (
                                <DownloadPdfButton handleDownload={handleDownloadRCSS} isDownloading={isDownloadingSS} title={'Aperçu de la déclaration à la Sécurité Sociale'} />
                            )}
                        </Box>

                        <SGSForm.Row>
                            <SGSForm.Col>
                                <SGSFormInput variant="outlined" name="certification.fullname" label="Nom complet" disabled />
                            </SGSForm.Col>
                        </SGSForm.Row>

                        <SGSForm.Row>
                            <SGSForm.Col>
                                <SGSFormInput variant="outlined" name="certification.email" label="Email" disabled />
                            </SGSForm.Col>
                        </SGSForm.Row>

                        <SGSForm.Row>
                            <SGSForm.Col>
                                <SGSCheckboxCtr
                                    name="certification.isCertified"
                                    label="Le responsable du personnel certifie la présente déclaration et atteste que le participant figurait sur les contrôles de la société à la date de l'arrêt de travail."
                                />
                            </SGSForm.Col>
                        </SGSForm.Row>
                    </Grid>
                </Grid>
            </SGSFormLayout>
        </FormProvider>
    );
};

export default FormCertification;
